import React, { useState, useEffect } from "react";
import { updateLocationRanking } from "services/rankingService";
import * as Sentry from "@sentry/react";

const PlayerRankings = ({
  locationRankings = [],
  loading = false,
  isAdmin = false,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [editedPlayer, setEditedPlayer] = useState({});
  const [localRankings, setLocalRankings] = useState([]);

  useEffect(() => {
    setLocalRankings(locationRankings);
  }, [locationRankings]);

  const openModal = (player) => {
    setCurrentPlayer(player);
    setEditedPlayer({ playerName: player.playerName, ranking: player.ranking });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setCurrentPlayer(null);
    setEditedPlayer({});
  };

  const handleSave = async () => {
    try {
      await updateLocationRanking(currentPlayer.id, editedPlayer);

      // 👇 Update local state directly
      setLocalRankings((prev) =>
        prev.map((p) =>
          p.id === currentPlayer.id ? { ...p, ...editedPlayer } : p
        )
      );

      closeModal();
    } catch (error) {
      Sentry.captureException(error);
      console.error("Erreur lors de la mise à jour du classement :", error);
      alert("Échec de la mise à jour.");
    }
  };

  if (loading) {
    return (
      <p className="text-center text-white">Chargement du classement...</p>
    );
  }

  if (localRankings.length === 0) {
    return (
      <p className="text-center text-gray-300">Aucun classement disponible.</p>
    );
  }

  return (
    <div className="max-w-xl mx-4 md:mx-auto mt-8 bg-gray-700 shadow-xl text-white rounded-md border border-gray-500">
      <h2 className="text-2xl font-bold p-4 text-white text-center bg-sky-900 rounded-t-md border-b border-gray-500">
        Classement des joueurs actifs
      </h2>
      <div className="p-6">
        <div
          className={`grid font-semibold border-b border-gray-600 pb-2 mb-2 ${
            isAdmin ? "grid-cols-3" : "grid-cols-2"
          }`}
        >
          <div className="text-amber-500">Joueur</div>
          <div className="text-right pr-2 text-amber-500">Classement</div>
          {isAdmin && <div className="text-right pr-6 text-amber-500">Modifier</div>}
        </div>

        <div className="max-h-[600px] overflow-y-auto pr-1">
          <ul>
            {localRankings.map((player) => (
              <li
                key={player.id}
                className={`grid py-2 border-b border-gray-800 items-center ${
                  isAdmin ? "grid-cols-3" : "grid-cols-2"
                }`}
              >
                <span>{player.playerName}</span>
                <span className="text-right font-medium text-amber-300 pr-2">
                  {player.ranking}
                </span>
                {isAdmin && (
                  <div className="text-right">
                    <button
                      onClick={() => openModal(player)}
                      className="bg-blue-600 hover:bg-blue-500 p-1 mr-2 rounded text-white text-sm"
                    >
                      📝
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>

        {modalOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
            <div className="bg-gray-800 p-6 rounded shadow-xl w-full max-w-md space-y-4">
              <h3 className="text-xl font-bold text-white">
                Modifier le classement
              </h3>

              <div>
                <label className="block text-sm mb-1 text-gray-300">Nom</label>
                <input
                  type="text"
                  value={editedPlayer.playerName}
                  onChange={(e) =>
                    setEditedPlayer({
                      ...editedPlayer,
                      playerName: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600"
                />
              </div>

              <div>
                <label className="block text-sm mb-1 text-gray-300">
                  Classement
                </label>
                <input
                  type="number"
                  value={editedPlayer.ranking}
                  onChange={(e) =>
                    setEditedPlayer({
                      ...editedPlayer,
                      ranking: parseInt(e.target.value, 10),
                    })
                  }
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 bg-gray-500 hover:bg-gray-400 text-white rounded"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded"
                >
                  Sauvegarder
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerRankings;
